/**
 * Seal Token 缓存管理器
 * 
 * 功能：
 * 1. 全局共享 Token 缓存
 * 2. 自动检查有效期
 * 3. 过期自动刷新
 * 4. 避免重复请求
 * 
 * @author 小华同学AI
 */

import { exchangeSealToken } from '@/api/seal/auth'
import { getSealTokenConfig, LOG_PREFIX } from '@/config/seal.config'
import { useEnterpriseStore } from '@/stores/enterprise'

interface TokenCache {
  token: string
  expireTime: string
  expireMinutes: number
}

class SealTokenManager {
  private cache: TokenCache | null = null
  private isRefreshing = false
  private refreshPromise: Promise<string> | null = null

  /**
   * 解析 JWT payload
   */
  private parseJwtPayload(token: string): Record<string, any> | null {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null

      const payload = parts[1]
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )

      return JSON.parse(jsonPayload)
    } catch (error) {
      console.error('[TokenManager] 解析 JWT 失败:', error)
      return null
    }
  }

  /**
   * 检查 Token 是否即将过期（提前 10 分钟刷新）
   */
  private isTokenExpiringSoon(token: string): boolean {
    const payload = this.parseJwtPayload(token)
    if (!payload?.exp) return true

    const expTime = payload.exp * 1000 // 转换为毫秒
    const now = Date.now()
    const tenMinutes = 10 * 60 * 1000 // 提前 10 分钟刷新

    return expTime - now < tenMinutes
  }

  /**
   * 从缓存或 localStorage 获取 Token
   */
  private getCachedToken(): TokenCache | null {
    // 优先使用内存缓存
    if (this.cache) {
      return this.cache
    }

    // 尝试从 localStorage 恢复
    try {
      const token = localStorage.getItem('seal_token')
      const expireTime = localStorage.getItem('seal_token_expire_time')
      const expireMinutes = localStorage.getItem('seal_token_expire_minutes')

      if (token && expireTime && expireMinutes) {
        this.cache = {
          token,
          expireTime,
          expireMinutes: Number(expireMinutes)
        }
        return this.cache
      }
    } catch (error) {
      console.error('[TokenManager] 从 localStorage 恢复 Token 失败:', error)
    }

    return null
  }

  /**
   * 保存 Token 到缓存和 localStorage
   */
  private saveToken(tokenData: TokenCache): void {
    this.cache = tokenData
    
    try {
      localStorage.setItem('seal_token', tokenData.token)
      localStorage.setItem('seal_token_expire_time', tokenData.expireTime)
      localStorage.setItem('seal_token_expire_minutes', String(tokenData.expireMinutes))
    } catch (error) {
      console.error('[TokenManager] 保存 Token 到 localStorage 失败:', error)
    }
  }

  /**
   * 清除 Token 缓存
   */
  clearToken(): void {
    this.cache = null
    this.isRefreshing = false
    this.refreshPromise = null
    
    try {
      localStorage.removeItem('seal_token')
      localStorage.removeItem('seal_token_expire_time')
      localStorage.removeItem('seal_token_expire_minutes')
    } catch (error) {
      console.error('[TokenManager] 清除 Token 失败:', error)
    }
  }

  /**
   * 检查缓存的 Token 中的企业上下文是否与当前身份一致
   * 
   * 防止以下场景：
   * - 用户从企业身份切换到个人身份，但旧 Token（含企业ID）仍在缓存中
   * - 导致 iframe 内的 API 调用使用了错误的企业上下文
   */
  private isTokenIdentityMismatch(token: string): boolean {
    try {
      const enterpriseStore = useEnterpriseStore()
      const payload = this.parseJwtPayload(token)
      if (!payload) return true // 无法解析，视为不匹配

      const tokenEnterpriseId = payload.enterprise_id ?? null
      const isPersonal = enterpriseStore.isPersonalIdentity
      const currentEnterpriseId = isPersonal ? null : (enterpriseStore.currentEnterprise?.id ?? null)

      // 个人身份但 Token 含企业ID → 不匹配
      if (isPersonal && tokenEnterpriseId != null) {
        console.warn(`${LOG_PREFIX} ⚠️ 身份不匹配: 当前=个人, Token含企业ID=${tokenEnterpriseId}`)
        return true
      }
      // 企业身份但 Token 不含企业ID → 不匹配
      if (!isPersonal && tokenEnterpriseId == null && currentEnterpriseId != null) {
        console.warn(`${LOG_PREFIX} ⚠️ 身份不匹配: 当前=企业(${currentEnterpriseId}), Token无企业ID`)
        return true
      }
      // 企业身份但 Token 的企业ID与当前不同 → 不匹配
      if (!isPersonal && tokenEnterpriseId != null && currentEnterpriseId != null
          && Number(tokenEnterpriseId) !== Number(currentEnterpriseId)) {
        console.warn(`${LOG_PREFIX} ⚠️ 身份不匹配: 当前企业=${currentEnterpriseId}, Token企业=${tokenEnterpriseId}`)
        return true
      }

      return false
    } catch (error) {
      console.error(`${LOG_PREFIX} 身份校验异常:`, error)
      return true // 异常时视为不匹配，强制刷新
    }
  }

  /**
   * 获取 Seal Token（带缓存和自动刷新）
   * 
   * 逻辑：
   * 1. 检查缓存是否存在且有效
   * 2. 校验 Token 中的企业上下文与当前身份是否一致
   * 3. 有效且一致则直接返回
   * 4. 无效、不一致或不存在则刷新
   * 5. 防止并发刷新
   */
  async getToken(): Promise<string> {
    console.log(`${LOG_PREFIX} 📦 获取 Seal Token...`)

    // 1. 检查缓存
    const cached = this.getCachedToken()
    
    if (cached) {
      // 2. 检查 Token 中的身份是否与当前身份一致
      if (this.isTokenIdentityMismatch(cached.token)) {
        console.log(`${LOG_PREFIX} 🔄 Token 身份上下文不匹配，清除缓存并重新获取`)
        this.clearToken()
      // 3. 检查是否即将过期
      } else if (!this.isTokenExpiringSoon(cached.token)) {
        console.log(`${LOG_PREFIX} ✅ 使用缓存的 Token（有效期至: ${cached.expireTime}）`)
        return cached.token
      } else {
        console.log(`${LOG_PREFIX} ⏰ Token 即将过期，需要刷新`)
      }
    } else {
      console.log(`${LOG_PREFIX} 📭 缓存中没有 Token，需要获取`)
    }

    // 3. 刷新 Token（防止并发）
    if (this.isRefreshing && this.refreshPromise) {
      console.log(`${LOG_PREFIX} ⏳ 正在刷新中，等待刷新完成...`)
      return this.refreshPromise
    }

    this.isRefreshing = true
    this.refreshPromise = this.refreshToken()

    try {
      const newToken = await this.refreshPromise
      return newToken
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * 刷新 Token（调用后端接口）
   */
  private async refreshToken(): Promise<string> {
    try {
      const config = getSealTokenConfig('pc')
      
      // 🔧 使用 isPersonalIdentity 显式判断身份类型，而非仅靠 currentEnterprise?.id 是否为空
      // 防止 currentEnterprise 因时序/初始化问题残留旧值
      const enterpriseStore = useEnterpriseStore()
      const enterpriseId = enterpriseStore.isPersonalIdentity
        ? undefined
        : enterpriseStore.currentEnterprise?.id
      
      console.log(`${LOG_PREFIX} 🔄 调用后端接口刷新 Token... 身份类型: ${enterpriseStore.isPersonalIdentity ? '个人' : '企业'}, enterpriseId: ${enterpriseId || '无'}`)
      
      // 🔧 传递企业ID到后端，确保 Token 中包含企业信息
      const response = await exchangeSealToken(enterpriseId, config.expireMinutes)
      
      if (!response || !response.token) {
        throw new Error('Token 响应格式错误')
      }

      // 保存到缓存
      this.saveToken({
        token: response.token,
        expireTime: response.expireTime,
        expireMinutes: response.expireMinutes
      })

      console.log(`${LOG_PREFIX} ✅ Token 刷新成功（有效期至: ${response.expireTime}）`)
      
      return response.token
    } catch (error: any) {
      console.error(`${LOG_PREFIX} ❌ Token 刷新失败:`, error)
      
      // 🔧 安全修复：区分错误类型，避免误判
      // 如果是主 Token 过期（401），不要清除 Seal Token 缓存，让用户自然重新登录
      // 如果是其他错误（网络、限流等），清除缓存
      const errorMsg = error?.message || error?.toString() || ''
      const isAuthError = errorMsg.includes('401') || 
                         errorMsg.includes('登录') || 
                         errorMsg.includes('过期') ||
                         errorMsg.includes('未登录')
      
      if (!isAuthError) {
        // 非认证错误，清除缓存
        this.clearToken()
      } else {
        console.log(`${LOG_PREFIX} ⚠️ 主 Token 过期，保留 Seal Token 缓存，等待用户重新登录`)
      }
      
      throw error
    }
  }

  /**
   * 强制刷新 Token（忽略缓存）
   */
  async forceRefresh(): Promise<string> {
    console.log(`${LOG_PREFIX} 🔄 强制刷新 Token...`)
    this.clearToken()
    return this.getToken()
  }
}

// 导出单例
export const sealTokenManager = new SealTokenManager()

// 导出便捷方法
export const getSealToken = () => sealTokenManager.getToken()
export const clearSealToken = () => sealTokenManager.clearToken()
export const forceRefreshSealToken = () => sealTokenManager.forceRefresh()
